import { useEffect, useMemo, useState } from "react";
import { Panel, Select } from "../components/controls";
import { loadTheme, saveTheme, type ThemeChoice } from "../theme";
import { useLive, useServices } from "../context";

export function MyGear() {
  const { controller } = useServices();
  const midi = useLive((s) => s.midi);
  const devices = useLive((s) => s.midiDevices);
  const channel = useLive((s) => s.midiChannel);
  const log = useLive((s) => s.midiLog);
  const audio = useLive((s) => s.audio);
  const outputs = useLive((s) => s.outputs);
  const outputId = useLive((s) => s.outputDeviceId);
  // `audio` is a deliberate dependency: capabilities include the context's channels/latency once it exists.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const caps = useMemo(() => controller.capabilities(), [controller, audio]);
  const [, force] = useState(0);
  const [theme, setTheme] = useState<ThemeChoice>(loadTheme);

  useEffect(() => { void controller.refreshOutputs(); }, [controller, audio]);
  const ctx = controller.audioContext;
  const maxCh = ctx?.destination.maxChannelCount ?? 0;
  const sinkSupported = caps.find((c) => c.id === "sinkid")?.status === "supported";
  const inputs = devices.filter((d) => d.kind === "input");
  const outs = devices.filter((d) => d.kind === "output");

  return (
    <div className="workspace-grid gear">
      <Panel title="MIDI devices">
        {midi.state === "ready" ? <p className="status ok">MIDI enabled.</p>
          : midi.state === "idle" ? <button type="button" className="primary" onClick={() => void controller.enableMidi()}>Enable MIDI</button>
          : midi.state === "requesting" ? <p role="status">Waiting for browser permission…</p>
          : <p className="status warn" role="alert">{midi.reason}</p>}
        {(midi.state === "denied" || midi.state === "error") && <button type="button" onClick={() => void controller.enableMidi()}>Try again</button>}
        {midi.state === "ready" && (
          <>
            <h3>Inputs</h3>
            {inputs.length === 0 ? <p className="hint">No MIDI inputs found. Connect a device — the list updates automatically.</p> : (
              <ul className="device-list">
                {inputs.map((d) => (
                  <li key={d.id}>
                    <label>
                      <input type="checkbox" checked={controller.midi.isInputEnabled(d.id)} disabled={!d.connected}
                        onChange={(e) => { controller.midi.setInputEnabled(d.id, e.currentTarget.checked); force((x) => x + 1); }} />
                      {d.name} {d.manufacturer && <span className="hint">({d.manufacturer})</span>} <span className={`status ${d.connected ? "ok" : "muted"}`}>{d.connected ? "connected" : "disconnected"}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <Select label="Keyboard channel" value={channel} options={[{ value: "omni" as const, label: "All channels (omni)" }, ...Array.from({ length: 16 }, (_, i) => ({ value: i + 1, label: `Channel ${i + 1}` }))]} onChange={(v) => controller.setMidiChannel(v)} />
            <p className="hint">In omni mode, notes on channel 10 play the drum kit (General MIDI convention). Sustain pedal (CC64) is supported. Pitch bend and MIDI learn are planned.</p>
            <h3>Outputs</h3>
            {outs.length === 0 ? <p className="hint">No MIDI outputs found.</p> : <ul className="device-list">{outs.map((d) => <li key={d.id}>{d.name} <span className={`status ${d.connected ? "ok" : "muted"}`}>{d.connected ? "connected" : "disconnected"}</span></li>)}</ul>}
            <p className="hint">Panic sends All Sound Off / All Notes Off to every connected output. Sending sequences to outputs is planned.</p>
          </>
        )}
        <p className="hint">MIDI carries notes and controls, not audio: to hear a keyboard's own sounds, connect its audio outputs to your interface or speakers.</p>
      </Panel>

      <Panel title="MIDI monitor" actions={<button type="button" onClick={() => controller.clearMidiLog()} disabled={!log.length}>Clear</button>}>
        {log.length === 0 ? <p className="hint">Incoming MIDI messages appear here.</p> : (
          <ol className="midi-log" aria-label="Recent MIDI messages, newest first">
            {log.slice(0, 60).map((l) => <li key={l.id}><code>{l.text}</code> <span className="hint">{l.port}</span></li>)}
          </ol>
        )}
      </Panel>

      <Panel title="Audio output">
        {audio !== "running" ? <button type="button" className="primary" onClick={() => void controller.startAudio()}>Start audio to inspect outputs</button> : (
          <>
            {sinkSupported ? (
              <Select label="Output device" value={outputId} options={[{ value: "default", label: "System default" }, ...outputs.filter((o) => o.deviceId !== "default").map((o) => ({ value: o.deviceId, label: o.label }))]} onChange={(id) => void controller.setOutput(id)} />
            ) : <p className="hint">This browser plays through the system default output; choosing a device needs a Chromium-based browser.</p>}
            <h3>Channel test</h3>
            <p>The current device reports <strong>{maxCh}</strong> output channel{maxCh === 1 ? "" : "s"}. Play a tone on each channel to check where it arrives on your interface or mixer.</p>
            <div className="channel-test">
              {Array.from({ length: Math.min(maxCh, 32) }, (_, i) => (
                <button key={i} type="button" onClick={() => void controller.testChannel(i)}>Ch {i + 1}</button>
              ))}
            </div>
            <p className="hint">Results depend on browser, operating system and driver, and are not yet verified on real hardware. Please record what you observe in docs/hardware-test-log.md.</p>
          </>
        )}
      </Panel>

      <Panel title="Browser capabilities">
        <ul className="caps">
          {caps.map((c) => (
            <li key={c.id} className={`cap ${c.status}`}>
              <strong>{c.label}</strong> <span className={`status ${c.status === "supported" ? "ok" : c.status === "partial" ? "warn" : "muted"}`}>{c.status}</span>
              <div className="hint">{c.detail}</div>
            </li>
          ))}
        </ul>
      </Panel>
      <Panel title="Display">
        <Select label="Theme" value={theme} options={[{ value: "system" as ThemeChoice, label: "Follow system" }, { value: "dark" as ThemeChoice, label: "Dark" }, { value: "light" as ThemeChoice, label: "Light" }]}
          onChange={(t) => { setTheme(t); saveTheme(t); }} />
        <p className="hint">Saved for this browser only. Dark is easier on the eyes on stage; light can be easier to read in daylight.</p>
      </Panel>
    </div>
  );
}
